import { useState } from "react";
import {
  Button,
  Group,
  Text,
  NumberInput,
  Select,
  Stack,
  Popover,
  Badge,
  Grid,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconFilter } from "@tabler/icons-react";
import type { ApartmentSearchParams, FilterOptions } from "@/api/types";

interface ApartmentFiltersProps {
  filters: ApartmentSearchParams;
  filterOptions: FilterOptions | undefined;
  onChange: (filters: Partial<ApartmentSearchParams>) => void;
  onClearAll: () => void;
  activeFilterCount: number;
}

export function ApartmentFilters({
  filters,
  filterOptions,
  onChange,
  onClearAll,
  activeFilterCount,
}: ApartmentFiltersProps) {
  const [opened, setOpened] = useState(false);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={400}>
      <Popover.Target>
        <Button
          variant={opened ? "filled" : "default"}
          color={opened ? "primary" : undefined}
          leftSection={<IconFilter size={18} />}
          onClick={() => setOpened((o) => !o)}
          styles={{
            root: {
              backgroundColor: opened ? undefined : "#16213e",
              borderColor: "#0f3460",
            },
          }}
        >
          Filters
          {activeFilterCount > 0 && (
            <Badge size="sm" circle ml="xs" color="white" c="primary">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </Popover.Target>

      <Popover.Dropdown bg="#16213e" style={{ borderColor: "#0f3460" }}>
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={600}>Filters</Text>
            {activeFilterCount > 0 && (
              <Button variant="subtle" size="xs" color="primary" onClick={onClearAll}>
                Clear all
              </Button>
            )}
          </Group>

          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Price Range (kr/month)
            </Text>
            <Group grow>
              <NumberInput
                placeholder="Min"
                value={filters.min_rent ?? ""}
                onChange={(val) => onChange({ min_rent: val ? Number(val) : undefined })}
                hideControls
              />
              <NumberInput
                placeholder="Max"
                value={filters.max_rent ?? ""}
                onChange={(val) => onChange({ max_rent: val ? Number(val) : undefined })}
                hideControls
              />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Size (m²)
            </Text>
            <Group grow>
              <NumberInput
                placeholder="Min"
                value={filters.min_size ?? ""}
                onChange={(val) => onChange({ min_size: val ? Number(val) : undefined })}
                hideControls
              />
              <NumberInput
                placeholder="Max"
                value={filters.max_size ?? ""}
                onChange={(val) => onChange({ max_size: val ? Number(val) : undefined })}
                hideControls
              />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Rooms
            </Text>
            <Group grow>
              <NumberInput
                placeholder="Min"
                value={filters.min_rooms ?? ""}
                onChange={(val) => onChange({ min_rooms: val ? Number(val) : undefined })}
                hideControls
                step={0.5}
              />
              <NumberInput
                placeholder="Max"
                value={filters.max_rooms ?? ""}
                onChange={(val) => onChange({ max_rooms: val ? Number(val) : undefined })}
                hideControls
                step={0.5}
              />
            </Group>
          </Stack>

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="City"
                placeholder="All cities"
                value={filters.city ?? null}
                onChange={(val) => onChange({ city: val || undefined })}
                data={filterOptions?.cities ?? []}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Area"
                placeholder="All areas"
                value={filters.area ?? null}
                onChange={(val) => onChange({ area: val || undefined })}
                data={filterOptions?.areas ?? []}
                clearable
              />
            </Grid.Col>
          </Grid>

          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Availability
            </Text>
            <Group grow>
              <DateInput
                placeholder="From"
                value={filters.available_from ? new Date(filters.available_from) : null}
                onChange={(date) => {
                  const dateVal = date as Date | null;
                  onChange({
                    available_from: dateVal ? dateVal.toISOString().split("T")[0] : undefined,
                  });
                }}
                clearable
              />
              <DateInput
                placeholder="Until"
                value={filters.available_until ? new Date(filters.available_until) : null}
                onChange={(date) => {
                  const dateVal = date as Date | null;
                  onChange({
                    available_until: dateVal ? dateVal.toISOString().split("T")[0] : undefined,
                  });
                }}
                clearable
              />
            </Group>
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
